/**
 * UpgradeModal — shown when a free-tier user hits a plan limit.
 * Renders in a Portal over the page so it works from any route.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
}

const BENEFITS = [
  'Unlimited books',
  'Full-length novels (all chapters)',
  'In-browser editing & revision',
  'EPUB & DOCX export',
  'Priority generation queue',
];

export function UpgradeModal({ open, onClose }: UpgradeModalProps) {
  // Prevent background scroll while modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          ✕
        </button>

        {/* Current plan badge */}
        <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />
          Free Plan
        </div>

        <h2
          id="upgrade-modal-title"
          className="text-xl font-bold text-foreground mb-2"
        >
          Upgrade to Create More Books
        </h2>

        <p className="text-sm text-muted-foreground mb-5">
          Your free plan includes{' '}
          <span className="font-semibold text-foreground">1 book</span>. You've
          reached that limit. Upgrade to unlock unlimited book creation and the
          full writing experience.
        </p>

        {/* Benefits list */}
        <ul className="mb-6 space-y-2">
          {BENEFITS.map((benefit) => (
            <li key={benefit} className="flex items-center gap-2 text-sm text-foreground">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-bold">
                ✓
              </span>
              {benefit}
            </li>
          ))}
        </ul>

        {/* CTAs */}
        <div className="flex flex-col gap-2">
          {/* Upgrade Now navigates to /pricing. No onClose — the component
              unmounts naturally on navigation, avoiding double-history entries
              (important when onClose is wired to a back-navigation like /dashboard). */}
          <Link
            to="/pricing"
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-center text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Upgrade Now →
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
